// The Certify HTTP client (packages/shared/src/http/certify.ts; docs/design/
// slice4-certify-ux.md). Every response parses through the SAME shared Zod schema
// apps/api validates against (app-engineering §2 "parse, don't validate"; the Slice-2
// wire-drift lesson) - never a re-derived client-side shape for a LANDED contract.
//
// LANDED routes (CERTIFY_ROUTES, packages/shared/src/http/certify.ts:28-34): scan,
// assess, certificate (upload), health, and the public trustPagePublic (served by
// apps/api directly per ADR-0020 decision 5 - this client never fetches it; apps/web
// only LINKS to the canonical public URL, §6.1/§6.3 of the ux spec).
//
// PROPOSED routes (every other export below): the ux spec's own §9 items 9-11 name
// these as genuinely open contract-change requests for the tech-lead/backend-engineer
// - "CERTIFY_ROUTES simply does not cover them yet" (ux spec §7.1 item 9). This file
// builds against those SAME illustrative shapes (never a fresh invention by this lane)
// so the dashboard/data-room/trust-page screens have a real, testable seam to call;
// each is commented PROPOSED at its declaration and flagged again in the dispatch
// return. The VALUE shapes these proposed envelopes carry are never invented - they
// reuse the landed domain schemas (ScanResult, Assessment, Certificate,
// BuilderHealthCheck, TrustPageView, BadgeState, FrameworkKey) throughout; only the
// wrapper envelope and the route's existence are illustrative.
import { z } from "zod";
import {
  Assessment,
  BadgeState,
  BuilderHealthCheck,
  Certificate,
  FrameworkKey,
  NonEmptyString,
  ScanResult,
  Timestamp,
  TrustPageView,
} from "@nightcrew/shared";
import type { AppId, Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

// ============================================================================
// LANDED (http/certify.ts:28-34)
// ============================================================================

/** POST /v1/apps/:appId/certify/scan (http/certify.ts:29,40-50). */
export const CertifyScanResponse = z.object({ results: z.array(ScanResult) });
export type CertifyScanResponse = z.infer<typeof CertifyScanResponse>;

export const runScan = (
  appId: AppId,
  scanners?: readonly string[],
): Promise<Result<CertifyScanResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/certify/scan`, CertifyScanResponse, {
    scanners: scanners === undefined ? undefined : [...scanners],
  });

/** POST /v1/apps/:appId/certify/assess (http/certify.ts:30,52-60). */
export const runAssessment = (
  appId: AppId,
  framework: FrameworkKey,
): Promise<Result<Assessment, ClientError>> =>
  apiPost(`/v1/apps/${appId}/certify/assess`, Assessment, { framework });

/** GET /v1/apps/:appId/certify/health (http/certify.ts:32,81-85). Independently
 * loadable, per the ux spec §4.1 - a real live check, not gated on a prior "run". */
export const getBuilderHealth = (appId: AppId): Promise<Result<BuilderHealthCheck, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/certify/health`, BuilderHealthCheck);

/**
 * POST /v1/apps/:appId/certify/certificate (http/certify.ts:31,62-79) - multipart. The
 * platform's first sensitive-upload path (ADR-0020 decision 3/4): `fileRef` and
 * `verifyState` are assigned server-side and never sent by the client (http/
 * certify.ts:65-67). Uses `apiFetch` directly (not `apiPost`, which always JSON-encodes)
 * so the body stays a real `FormData` - see src/api/client.ts's FormData carve-out.
 */
export interface CertificateUploadFields {
  readonly issuer: string;
  readonly scope: string;
  readonly issuedAt: string;
  readonly expiresAt: string;
  readonly file: File;
}

export const uploadCertificate = (
  appId: AppId,
  fields: CertificateUploadFields,
): Promise<Result<Certificate, ClientError>> => {
  const form = new FormData();
  form.set("issuer", fields.issuer);
  form.set("scope", fields.scope);
  form.set("issuedAt", fields.issuedAt);
  form.set("expiresAt", fields.expiresAt);
  form.set("file", fields.file);
  return apiFetch(`/v1/apps/${appId}/certify/certificate`, Certificate, {
    method: "POST",
    body: form,
  });
};

/**
 * The canonical PUBLIC trust-page URL (ADR-0020 decision 5, `GET /v1/trust/:slug`,
 * http/certify.ts:33) - a pure string builder, never a fetch (the public page is a
 * server-rendered `apps/api` route this client never calls; it only links to it,
 * ux spec §2.2/§6.1). ASSUMED same-origin (flagged in the dispatch return): no
 * production API-origin config exists anywhere in apps/web yet (verified: no
 * `VITE_*`/`import.meta.env` API-origin reference in this package) - `window.location
 * .origin` mirrors the dev-proxy's own same-origin-relative-path convention every other
 * `/v1/...` call in this client already uses, but the real production topology
 * (same-origin reverse proxy vs. a separate api.* host) needs tech-lead/infra
 * confirmation before this is load-bearing for a founder-shared link.
 */
export const publicTrustPageUrl = (slug: string): string =>
  `${window.location.origin}/v1/trust/${encodeURIComponent(slug)}`;

// ============================================================================
// PROPOSED (no landed backing - ux spec §7.1 items 9-12; flagged, not invented)
// ============================================================================

/**
 * PROPOSED `GET /v1/apps/:appId/certify` - the dashboard's own aggregate read (ux spec
 * §4.3, §7.1 item 9: "CERTIFY_ROUTES simply does not cover them yet"). `badge` mirrors
 * the landed `BadgeState` (model/certify.ts:200-201) - the server computes it via
 * `packages/certify`'s pure `deriveBadgeState`; this client only ever DISPLAYS it, never
 * recomputes the transition logic itself (keep-derived-state-acyclic, app-engineering
 * §2 - a client-side reimplementation would need history this client does not hold).
 */
export const CertifySummaryResponse = z.object({
  hasRunBefore: z.boolean(),
  lastRunAt: Timestamp.nullable(),
  badge: BadgeState,
  scans: z.array(ScanResult),
  assessments: z.array(Assessment),
});
export type CertifySummaryResponse = z.infer<typeof CertifySummaryResponse>;

export const getCertifySummary = (
  appId: AppId,
): Promise<Result<CertifySummaryResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/certify`, CertifySummaryResponse);

/**
 * PROPOSED `POST /v1/apps/:appId/certify/gdpr-routing/confirm` - the founder's one-tap
 * confirm on a GDPR item routed to them (ux spec §4.4). No route or shape is named
 * anywhere in canon for this action (the ux spec only flags it as a REQUIRED
 * `audit_log` write for the backend-engineer/tech-lead, §4.4 point 4) - this is this
 * lane's own illustrative shape, flagged for reconciliation, not silently invented
 * against a confirmed contract (there is none to contradict).
 */
export const GdprRoutingConfirmResponse = z.object({ confirmed: z.literal(true) });
export type GdprRoutingConfirmResponse = z.infer<typeof GdprRoutingConfirmResponse>;

export const confirmGdprRoutingItem = (
  appId: AppId,
  controlKey: string,
): Promise<Result<GdprRoutingConfirmResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/certify/gdpr-routing/confirm`, GdprRoutingConfirmResponse, {
    controlKey,
  });

/**
 * PROPOSED `GET /v1/apps/:appId/certify/certificates` - the data room's list (ux spec
 * §5.2, §7.1 item 9).
 */
export const CertificateListResponse = z.object({ certificates: z.array(Certificate) });
export type CertificateListResponse = z.infer<typeof CertificateListResponse>;

export const listCertificates = (
  appId: AppId,
): Promise<Result<CertificateListResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/certify/certificates`, CertificateListResponse);

/**
 * PROPOSED evidence-detail GET + share-link POST - a NAMED CONTRACT GAP, not merely
 * PROPOSED (ux spec §5.3, §7.1 item 10: "NOT BACKED by CERTIFY_ROUTES... a contract-
 * change request for the tech-lead"). The landed `Certificate` schema carries no `id`
 * field (model/certify.ts:176-190) - `fileRef` (the private object-storage handle) is
 * the only unique-ish value available client-side, so it doubles as the route's
 * `documentId` here; a real backend would likely mint a stable, non-storage-coupled
 * certificate id instead - flagged for the tech-lead alongside the missing routes
 * themselves, not treated as settled.
 */
export const getCertificateDetail = (
  appId: AppId,
  documentId: string,
): Promise<Result<Certificate, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/certify/certificates/${encodeURIComponent(documentId)}`, Certificate);

export const ShareLinkResponse = z.object({ url: NonEmptyString });
export type ShareLinkResponse = z.infer<typeof ShareLinkResponse>;

export const createShareLink = (
  appId: AppId,
  documentId: string,
): Promise<Result<ShareLinkResponse, ClientError>> =>
  apiPost(
    `/v1/apps/${appId}/certify/certificates/${encodeURIComponent(documentId)}/share-link`,
    ShareLinkResponse,
    {},
  );

/**
 * PROPOSED trust-page management read (ux spec §6.3, §7.1 item 9). `preview` is the
 * landed `TrustPageView` shape (model/certify.ts:292-299) - what the public page shows,
 * or would show once published; `null` only when `hasCertifyResults` is false (§6.3
 * "no Certify results yet" state - nothing to preview). `eligibleFrameworks` narrows to
 * `qualifies: "yes"` results only (§6.1 point 2 - a `no`/`partial` result is never
 * publishable); `verifiedCertificateRefs` are the `fileRef`s of `verified`, in-date
 * certificates available to feature (same fileRef-as-identifier caveat as above).
 */
export const TrustPageManagementResponse = z.object({
  hasCertifyResults: z.boolean(),
  published: z.boolean(),
  publicSlug: NonEmptyString.nullable(),
  preview: TrustPageView.nullable(),
  /** Which framework `preview.programmaticReadiness` is about, if any - `TrustPageView`
   * itself carries no `framework` field (model/certify.ts:279-293), so this envelope
   * names it for display; `null` when no framework is currently featured. */
  featuredFramework: FrameworkKey.nullable(),
  eligibleFrameworks: z.array(FrameworkKey),
  verifiedCertificateRefs: z.array(NonEmptyString),
});
export type TrustPageManagementResponse = z.infer<typeof TrustPageManagementResponse>;

export const getTrustPageManagement = (
  appId: AppId,
): Promise<Result<TrustPageManagementResponse, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/certify/trust-page`, TrustPageManagementResponse);

/** PROPOSED single-block compose/publish request (ux spec §6.1 point 2, reconciled to
 * the landed `TrustPageView`'s ONE `programmaticReadiness` + ONE nullable
 * `verifiedCredential` - never plural, §7.1 item 11). */
export const PublishTrustPageRequest = z.object({
  featuredFramework: FrameworkKey.nullable(),
  featuredCertificateRef: NonEmptyString.nullable(),
  includeClaims: z.boolean(),
});
export type PublishTrustPageRequest = z.infer<typeof PublishTrustPageRequest>;

export const PublishTrustPageResponse = z.object({
  published: z.literal(true),
  publicSlug: NonEmptyString,
});
export type PublishTrustPageResponse = z.infer<typeof PublishTrustPageResponse>;

export const publishTrustPage = (
  appId: AppId,
  request: PublishTrustPageRequest,
): Promise<Result<PublishTrustPageResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/certify/trust-page/publish`, PublishTrustPageResponse, request);

export const UnpublishTrustPageResponse = z.object({ published: z.literal(false) });
export type UnpublishTrustPageResponse = z.infer<typeof UnpublishTrustPageResponse>;

export const unpublishTrustPage = (
  appId: AppId,
): Promise<Result<UnpublishTrustPageResponse, ClientError>> =>
  apiPost(`/v1/apps/${appId}/certify/trust-page/unpublish`, UnpublishTrustPageResponse, {});

// ============================================================================
// Shared client-side derivations (never a re-implementation of a server rule; each
// is documented against the landed schema it reads, and flagged where it stands in
// for a field the wire does not carry).
// ============================================================================

/**
 * "Needs your decision" (ux spec §4.4): a GDPR control whose remediation is a founder
 * decision, not a pure pass/fail. ASSUMED derivation, flagged (no canon doc names a
 * wire field for this - `ControlStatus` is the closed `pass | fail | na`,
 * model/certify.ts:71-72, with no fourth "needs decision" value to parse instead of
 * inventing this). A `manual_evidence` control (`checkType`, model/certify.ts:34 -
 * "manual-evidence controls close on an owner artifact") that reads `fail` with no
 * evidence yet is read as awaiting the founder, not a plain failure - this is this
 * lane's own reasoned mapping from the landed shape, not a confirmed contract, and is
 * flagged in the dispatch return for the tech-lead to confirm or correct.
 */
export const needsFounderDecision = (assessed: {
  readonly control: { readonly checkType: string };
  readonly result: { readonly status: string; readonly evidence: readonly unknown[] };
}): boolean =>
  assessed.control.checkType === "manual_evidence" &&
  assessed.result.status === "fail" &&
  assessed.result.evidence.length === 0;
